import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  Person24Regular,
  Mail24Regular,
  Call24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
  Shield24Regular,
  Clock24Regular,
  CheckmarkCircle24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { useAuth } from "../../auth/AuthContext";
import { ApiError, getUsers, type UserAdmin } from "../../users/usersApi";
import { APP_LOCALE } from "../../config/locale";

type PerfilAcesso = "Administrador" | "Utilizador" | "Equipa interna";
type EstadoUtilizador = "Ativo" | "Inativo" | "Pendente";

interface Utilizador {
  id: string;
  nome: string;
  email: string;
  telefone?: string;
  perfil: PerfilAcesso;
  estado: EstadoUtilizador;
  departamento?: string;
  ultimoAcesso: string;
  dataCriacao: string;
  avatar?: string;
}

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar utilizadores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os utilizadores.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const formatLastAccess = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";

  const now = new Date();
  const today = now.toDateString();
  const parsedDay = parsed.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const timeLabel = parsed.toLocaleTimeString(APP_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (parsedDay === today) {
    return `Hoje às ${timeLabel}`;
  }

  if (parsedDay === yesterday.toDateString()) {
    return `Ontem às ${timeLabel}`;
  }

  return parsed.toLocaleDateString(APP_LOCALE);
};

const mapPerfil = (roles: string[]): PerfilAcesso => {
  if (roles.some((role) => role === "Administrator")) return "Administrador";
  if (roles.some((role) => role === "TeamMember")) return "Equipa interna";
  return "Utilizador";
};

const mapEstado = (
  emailConfirmed: boolean | undefined,
  lockoutEnd?: string | null,
): EstadoUtilizador => {
  if (lockoutEnd) {
    const parsed = new Date(lockoutEnd);
    if (!Number.isNaN(parsed.getTime()) && parsed > new Date()) {
      return "Inativo";
    }
  }

  if (emailConfirmed === false) {
    return "Pendente";
  }

  return "Ativo";
};

const mapUserToView = (user: UserAdmin): Utilizador => {
  const fullName = [user.firstName, user.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");

  return {
    id: user.userId,
    nome: fullName || user.email,
    email: user.email,
    telefone: user.phoneNumber ?? undefined,
    perfil: mapPerfil(user.roles ?? []),
    estado: mapEstado(user.emailConfirmed, user.lockoutEnd),
    departamento: user.department ?? undefined,
    ultimoAcesso: formatLastAccess(user.lastModified),
    dataCriacao: formatDate(user.created),
    avatar: user.avatarUrl ?? undefined,
  };
};

export function UtilizadoresView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [utilizadores, setUtilizadores] = useState<Utilizador[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    PerfilAcesso | "Todos"
  >("Todos");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  const canCreateUser =
    user?.roles?.includes("Administrator") ?? false;

  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getUsers();
        if (!isMounted) return;

        const mapped = response.map(mapUserToView);
        setUtilizadores(mapped);
        setSelectedUserId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setUtilizadores([]);
        setSelectedUserId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredUtilizadores = useMemo(
    () =>
      utilizadores.filter((entry) => {
        const matchesSearch =
          entry.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          entry.email
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesFilter =
          selectedFilter === "Todos" ||
          entry.perfil === selectedFilter;
        return matchesSearch && matchesFilter;
      }),
    [utilizadores, searchQuery, selectedFilter],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredUtilizadores.length === 0) {
      setSelectedUserId(null);
      return;
    }

    const hasSelection = filteredUtilizadores.some(
      (entry) => entry.id === selectedUserId,
    );
    if (!hasSelection) {
      setSelectedUserId(filteredUtilizadores[0].id);
    }
  }, [filteredUtilizadores, isLoading, selectedUserId]);

  const selectedUser = filteredUtilizadores.find(
    (entry) => entry.id === selectedUserId,
  );

  const getPerfilColor = (perfil: PerfilAcesso) => {
    switch (perfil) {
      case "Administrador":
        return "bg-red-500/10 text-red-700 dark:text-red-400";
      case "Utilizador":
        return "bg-green-500/10 text-green-700 dark:text-green-400";
      case "Equipa interna":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-400";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-400";
    }
  };

  const getEstadoColor = (estado: EstadoUtilizador) => {
    switch (estado) {
      case "Ativo":
        return "bg-green-500/10 text-green-700 dark:text-green-400";
      case "Inativo":
        return "bg-red-500/10 text-red-700 dark:text-red-400";
      case "Pendente":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-400";
    }
  };

  const getEstadoIcon = (estado: EstadoUtilizador) => {
    switch (estado) {
      case "Ativo":
        return (
          <CheckmarkCircle24Regular className="text-green-600 dark:text-green-400" />
        );
      case "Inativo":
        return (
          <Dismiss24Regular className="text-red-600 dark:text-red-400" />
        );
      case "Pendente":
        return (
          <Clock24Regular className="text-yellow-600 dark:text-yellow-400" />
        );
    }
  };

  const actions = (
    <button
      className="px-4 py-2 bg-primary text-primary-foreground transition-all flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ borderRadius: "var(--radius-button)" }}
      onClick={() => navigate("/utilizadores/novo")}
      disabled={!canCreateUser}
      title={
        canCreateUser
          ? undefined
          : "Sem permissões para criar utilizadores."
      }
    >
      <Add20Regular className="w-5 h-5" />
      <span className="hidden sm:inline">Novo utilizador</span>
    </button>
  );

  return (
    <div className="flex flex-col h-full bg-background">
      <PageHeader title="Utilizadores" actions={actions} />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`flex-1 flex flex-col border-r border-[var(--border)] ${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          }`}
        >
          <div className="px-6 pt-6 pb-4 border-b border-[var(--border)] space-y-4 bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <input
                type="text"
                placeholder="Pesquisar utilizadores..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full h-10 pl-10 pr-4 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                style={{ borderRadius: "var(--radius)" }}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {["Todos", "Administrador", "Utilizador", "Equipa interna"].map((filter) => (
                <button
                  key={filter}
                  onClick={() =>
                    setSelectedFilter(
                      filter as PerfilAcesso | "Todos",
                    )
                  }
                  className={`px-3 py-1.5 text-sm transition-all ${
                    selectedFilter === filter
                      ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                      : "bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)]"
                  }`}
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="text-sm text-[var(--muted-foreground)]">
              {isLoading
                ? "A carregar utilizadores..."
                : `${filteredUtilizadores.length} utilizador${
                    filteredUtilizadores.length !== 1 ? "es" : ""
                  } encontrado${
                    filteredUtilizadores.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar utilizadores...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredUtilizadores.map((user) => (
                <button
                  key={user.id}
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setIsMobileDetailOpen(true);
                  }}
                  className={`w-full p-4 border-b border-[var(--border)] transition-colors text-left ${
                    selectedUserId === user.id
                      ? "bg-[var(--accent)] border-l-4 border-l-[var(--primary)]"
                      : "hover:bg-[var(--accent)] border-l-4 border-l-transparent"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 bg-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center flex-shrink-0"
                      style={{ borderRadius: "var(--radius-full)" }}
                    >
                      <span className="font-medium">
                        {user.nome
                          .split(" ")
                          .map((name) => name[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-card-foreground truncate">
                          {user.nome}
                        </h3>
                        <span
                          className={`text-xs px-2 py-0.5 flex-shrink-0 ${getEstadoColor(
                            user.estado,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {user.estado}
                        </span>
                      </div>
                      <p className="text-sm text-[var(--muted-foreground)] truncate mb-1">
                        {user.email}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 ${getPerfilColor(
                            user.perfil,
                          )}`}
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          {user.perfil}
                        </span>
                        {user.departamento && (
                          <span className="text-xs text-[var(--muted-foreground)]">
                            • {user.departamento}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading &&
              !loadError &&
              filteredUtilizadores.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                  <p className="text-[var(--muted-foreground)] text-center">
                    Nenhum utilizador encontrado
                  </p>
                </div>
              )}
          </div>
        </div>

      {selectedUser && (
        <div
          className={`w-full md:w-[440px] flex flex-col border-r border-[var(--border)] bg-card ${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          }`}
        >
          <div className="md:hidden flex items-center gap-3 p-4 border-b border-[var(--border)]">
            <button
              onClick={() => setIsMobileDetailOpen(false)}
              className="p-2 hover:bg-[var(--accent)] transition-colors"
              style={{ borderRadius: "var(--radius)" }}
            >
              <ArrowLeft24Regular className="text-card-foreground" />
            </button>
            <h2 className="text-card-foreground">Detalhes</h2>
          </div>

          <div className="p-6 border-b border-[var(--border)]">
            <div className="flex items-start gap-4 mb-4">
              <div
                className="w-16 h-16 bg-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center text-xl flex-shrink-0"
                style={{ borderRadius: "var(--radius-full)" }}
              >
                <span className="font-medium">
                  {selectedUser.nome
                    .split(" ")
                    .map((name) => name[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <h2 className="text-card-foreground mb-1 truncate text-xl">
                  {selectedUser.nome}
                </h2>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`text-xs px-2 py-1 ${getPerfilColor(
                      selectedUser.perfil,
                    )}`}
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {selectedUser.perfil}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 ${getEstadoColor(
                      selectedUser.estado,
                    )}`}
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {selectedUser.estado}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() =>
                navigate(`/utilizadores/editar/${selectedUser.id}`)
              }
              className="w-full h-10 bg-[var(--primary)] text-[var(--primary-foreground)] hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
              disabled={!canCreateUser}
              title={
                canCreateUser
                  ? undefined
                  : "Sem permissões para editar utilizadores."
              }
            >
              <Edit20Regular />
              Editar utilizador
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 border-b border-[var(--border)]">
              <h3 className="text-card-foreground mb-4 flex items-center gap-2">
                <Person24Regular />
                Informação de Contacto
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Mail24Regular className="text-[var(--muted-foreground)] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--muted-foreground)] mb-0.5">
                      Email
                    </p>
                    <p className="text-card-foreground break-all">
                      {selectedUser.email}
                    </p>
                  </div>
                </div>

                {selectedUser.telefone && (
                  <div className="flex items-start gap-3">
                    <Call24Regular className="text-[var(--muted-foreground)] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[var(--muted-foreground)] mb-0.5">
                        Telefone
                      </p>
                      <p className="text-card-foreground">
                        {selectedUser.telefone}
                      </p>
                    </div>
                  </div>
                )}

                {selectedUser.departamento && (
                  <div className="flex items-start gap-3">
                    <Shield24Regular className="text-[var(--muted-foreground)] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[var(--muted-foreground)] mb-0.5">
                        Departamento
                      </p>
                      <p className="text-card-foreground">
                        {selectedUser.departamento}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-b border-[var(--border)]">
              <h3 className="text-card-foreground mb-4 flex items-center gap-2">
                <Clock24Regular />
                Informação de Acesso
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--muted-foreground)]">
                    Último acesso
                  </span>
                  <span className="text-sm text-card-foreground">
                    {selectedUser.ultimoAcesso}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--muted-foreground)]">
                    Data de criação
                  </span>
                  <span className="text-sm text-card-foreground">
                    {selectedUser.dataCriacao}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--muted-foreground)]">
                    Estado
                  </span>
                  <div className="flex items-center gap-2">
                    {getEstadoIcon(selectedUser.estado)}
                    <span className="text-sm text-card-foreground">
                      {selectedUser.estado}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

        {!selectedUser && (
          <div className="hidden md:flex w-[440px] items-center justify-center bg-card border-r border-[var(--border)]">
            <div className="text-center px-6">
              <Person24Regular className="text-[var(--muted-foreground)] mx-auto mb-4" />
              <p className="text-[var(--muted-foreground)]">
                Selecione um utilizador para ver os detalhes
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
